import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../utils/auth';

type NavLeaf = {
  label: string;
  path: string;
  icon: JSX.Element;
  isActive: (pathname: string) => boolean;
};

type NavGroup = {
  label: string;
  icon: JSX.Element;
  items: NavLeaf[];
};

const icon = (d: string, extraPath?: string) => (
  <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d={d} />
    {extraPath && <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d={extraPath} />}
  </svg>
);

const leafIcon = (d: string, extraPath?: string) => (
  <svg className='w-4 h-4 shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d={d} />
    {extraPath && <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d={extraPath} />}
  </svg>
);

/**
 * Every destination the app has, in one place.
 *
 * A flat top-level button per destination stopped fitting once Stickers and Frames joined Filters,
 * Backgrounds and Milestone Badges — that is what broke the navbar. Fixed by grouping related
 * destinations under one dropdown each, so the number of TOP-LEVEL buttons stays constant as new
 * live-asset types are added: they go inside "Live Assets", not next to it.
 *
 * Dashboard.tsx keeps its own parallel list rather than importing this one — its cards need a
 * longer description and a stats line that don't belong in a compact nav dropdown — but every
 * destination here has a matching card there and vice versa. Add a new page in both places: a
 * navbar entry with no Dashboard card is unreachable from the homepage, and a card with no navbar
 * entry means there is no way back to it once you leave the dashboard.
 */
const STANDALONE: NavLeaf[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: icon('M3 12l9-9 9 9M4 10v10a1 1 0 001 1h5m8-11v10a1 1 0 01-1 1h-5'),
    isActive: (p) => p === '/dashboard',
  },
  {
    label: 'Products',
    path: '/products',
    icon: icon('M20 13V7a2 2 0 00-2-2H6a2 2 0 00-2 2v6m16 0v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6m16 0H4'),
    isActive: (p) => p === '/products',
  },
];

const GROUPS: NavGroup[] = [
  {
    label: 'Live Assets',
    icon: icon('M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'),
    items: [
      {
        label: 'Filters',
        path: '/live-filters',
        icon: leafIcon('M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'),
        isActive: (p) => p.startsWith('/live-filters'),
      },
      {
        label: 'Backgrounds',
        path: '/live-backgrounds',
        icon: leafIcon('M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'),
        isActive: (p) => p.startsWith('/live-backgrounds'),
      },
      {
        label: 'Stickers',
        path: '/live-stickers',
        icon: leafIcon('M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z', 'M12 3v6h6'),
        isActive: (p) => p.startsWith('/live-stickers'),
      },
      {
        label: 'Frames',
        path: '/live-frames',
        icon: leafIcon('M4 4h16v16H4V4zm3 3h10v10H7V7z'),
        isActive: (p) => p.startsWith('/live-frames'),
      },
      {
        label: 'Milestone Badges',
        path: '/live-milestone-badges',
        icon: leafIcon('M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z'),
        isActive: (p) => p.startsWith('/live-milestone-badges'),
      },
      {
        label: 'Top Fan Badges',
        path: '/live-badges/top-fan',
        icon: leafIcon('M5 16L3 6l5.5 4L12 4l3.5 6L21 6l-2 10H5zm0 3h14'),
        isActive: (p) => p.startsWith('/live-badges/top-fan'),
      },
      {
        label: 'Daily Streak Badges',
        path: '/live-badges/daily-streak',
        icon: leafIcon('M12 3c1 4 5 5.5 5 10a5 5 0 01-10 0c0-2.5 1.5-4 2.5-5 .3 2 1.2 3 2.5 3.5C11 9 11 6 12 3z'),
        isActive: (p) => p.startsWith('/live-badges/daily-streak'),
      },
      {
        label: 'PingTop Level Badges',
        path: '/live-badges/pingtop-level',
        icon: leafIcon('M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z'),
        isActive: (p) => p.startsWith('/live-badges/pingtop-level'),
      },
      {
        label: 'Live Quest',
        path: '/live-quest-assets',
        icon: leafIcon('M12 2l2.7 5.47 6.04.88-4.37 4.26 1.03 6.02L12 16.77 6.6 19.63l1.03-6.02L3.26 9.35l6.04-.88L12 2z'),
        isActive: (p) => p.startsWith('/live-quest-assets'),
      },
    ],
  },
  {
    label: 'Content',
    icon: icon('M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z'),
    items: [
      {
        label: 'Gift Animations',
        path: '/gift-animations',
        icon: leafIcon('M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664zM21 12a9 9 0 11-18 0 9 9 0 0118 0z'),
        isActive: (p) => p.startsWith('/gift-animations'),
      },
      {
        label: 'Announcements',
        path: '/announcements',
        icon: leafIcon('M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z'),
        isActive: (p) => p.startsWith('/announcements'),
      },
      {
        label: 'Entry Animations',
        path: '/products?category=LIVE',
        icon: leafIcon('M13 7l5 5m0 0l-5 5m5-5H6'),
        isActive: () => false, // query-string variant of /products; never shown as the "current" tab
      },
      {
        label: 'Premium Packs',
        path: '/products?category=THEMES_DECOR',
        icon: leafIcon('M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'),
        isActive: (p) => p.startsWith('/products/premium-bundles') || p.startsWith('/products/sticker-packs'),
      },
    ],
  },
];

const NavDropdown = ({ group, pathname, navigate }: { group: NavGroup; pathname: string; navigate: (p: string) => void }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isActive = group.items.some((item) => item.isActive(pathname));

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <div className='relative' ref={ref}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup='true'
        className={`flex items-center gap-2 px-4 py-2.5 font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap ${
          isActive ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {group.icon}
        <span className='hidden lg:inline'>{group.label}</span>
        <svg className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
        </svg>
      </button>

      {open && (
        <div className='absolute left-0 sm:right-0 sm:left-auto mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50'>
          {group.items.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-left transition-colors ${
                item.isActive(pathname) ? 'bg-orange-50 text-[#F42026]' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoginPage = location.pathname === '/';
  const pathname = location.pathname;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className='sticky top-0 bg-white p-4 border shadow-sm z-50'>
      <div className='flex flex-wrap sm:flex-nowrap justify-between items-center gap-3 max-w-7xl mx-auto'>
        {/* Logo/Title - Clickable to go home */}
        <button
          onClick={() => navigate('/dashboard')}
          className='text-2xl md:text-3xl font-bold hover:opacity-80 transition-opacity shrink-0'
        >
          <span className='bg-gradient-to-r from-[#FF9C15] to-[#F42026] bg-clip-text text-transparent'>Tezda</span>{' '}
          Assets
        </button>

        {/* Navigation - Only show when not on login page */}
        {!isLoginPage && (
          <div className='flex flex-wrap items-center gap-2 sm:gap-3 justify-end'>
            {STANDALONE.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex items-center gap-2 px-4 py-2.5 font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap ${
                  item.isActive(pathname)
                    ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {item.icon}
                <span className='hidden sm:inline'>{item.label}</span>
              </button>
            ))}

            {GROUPS.map((group) => (
              <NavDropdown key={group.label} group={group} pathname={pathname} navigate={navigate} />
            ))}

            <button
              onClick={handleLogout}
              className='flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap'
            >
              <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1'
                />
              </svg>
              <span className='hidden sm:inline'>Logout</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Navbar;
